import type { CrashContext } from "@/lib/crashLog";

export interface ClientCrashPayload {
  schema: "fahali.client-crash/v1";
  observedAt: string;
  origin: CrashContext["origin"];
  fatal: boolean;
  message: string;
  stack: string | null;
  componentStack: string | null;
}

const SECRET_PATTERNS: [RegExp, string][] = [
  [/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [REDACTED]"],
  [/sk_live_[A-Za-z0-9_-]+/gi, "sk_live_[REDACTED]"],
  [/\b(?:api[-_ ]?key|secret|token|password)\s*[:=]\s*[^\s,;]+/gi, "[CREDENTIAL REDACTED]"],
  [/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[EMAIL REDACTED]"],
  [/\b0x[a-fA-F0-9]{40}\b/g, "[WALLET REDACTED]"],
  [/(https?:\/\/[^\s?]+)\?[^\s)]+/gi, "$1?[QUERY REDACTED]"],
];

function safeText(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return "(unstringifiable thrown value)";
  }
}

export function scrubCrashText(value: unknown, maxLength: number): string {
  let text = safeText(value);
  for (const [pattern, replacement] of SECRET_PATTERNS) text = text.replace(pattern, replacement);
  return text.slice(0, maxLength);
}

export function buildCrashPayload(
  error: unknown,
  context: CrashContext,
  now = new Date(),
): ClientCrashPayload {
  const message = error instanceof Error ? error.message : error;
  const stack = error instanceof Error && error.stack ? scrubCrashText(error.stack, 6_000) : null;
  return {
    schema: "fahali.client-crash/v1",
    observedAt: now.toISOString(),
    origin: context.origin,
    fatal: Boolean(context.fatal),
    message: scrubCrashText(message, 700),
    stack,
    componentStack: context.componentStack ? scrubCrashText(context.componentStack, 3_000) : null,
  };
}

/** Best-effort browser telemetry. It must never throw on the crash path. */
export function sendCrashBeacon(error: unknown, context: CrashContext): void {
  try {
    if (typeof window === "undefined" || typeof navigator === "undefined") return;
    const body = JSON.stringify(buildCrashPayload(error, context));
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon("/api/client-errors", new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch("/api/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch {
    // A reporter is never allowed to become a second crash.
  }
}
